import styled from "@emotion/styled"
import React, { useEffect, useState } from "react"
import moment, { Moment } from "moment"
import "moment/locale/cs"
import { Tooltip, Button } from "antd"
import { usePathname } from "next/navigation"
import { CourseDates } from "../../lib/contentful/getCourseDates"
import { theme } from "../../styles/styles"

moment.locale("cs")

interface MiniScheduleCalendarProps {
  courseDates: CourseDates
  firstCourseDay?: Moment
}

const CalendarWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  width: 100%;
  font-size: 12px;

  .week-days {
    text-align: center;
  }
`

const DayCell = styled.div<{ isLesson?: boolean; isSkipped?: boolean; isToday?: boolean }>`
  padding: 6px 0;
  text-align: center;
  border-radius: 4px;
  border: ${({ isToday }) => (isToday ? "2px solid #01497c" : "1px solid #d9d9d9")};
  background-color: ${({ isSkipped, isLesson }) => (isSkipped ? theme.colors["main-color-red"] : isLesson ? theme.colors["main-color-blue"] : "transparent")};
  color: ${({ isSkipped, isLesson }) => (isSkipped || isLesson ? "#fff" : "inherit")};
  cursor: ${({ isLesson, isSkipped }) => (isLesson || isSkipped ? "pointer" : "default")};
`

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  align-items: center;
`

const daysOfWeek = moment.weekdaysShort(true) // ['Po', 'Út', ..., 'Ne']

const MiniScheduleCalendar: React.FC<MiniScheduleCalendarProps> = ({ courseDates, firstCourseDay }) => {
  const pathname = usePathname()

  const lessonSet = new Set(courseDates?.lessonDates?.map((d) => d.format("YYYY-MM-DD")))
  const skippedSet = new Set(courseDates?.skippedDates?.map((d) => d.format("YYYY-MM-DD")))

  const resolvedTime = firstCourseDay?.format("HH:mm")

  const today = moment()

  const getInitialMonth = () => {
    const hasToday = courseDates?.lessonDates?.some((d) => d.isSame(today, "day"))
    if (hasToday) return today.clone().startOf("month")
    return firstCourseDay?.clone().startOf("month") ?? courseDates?.lessonDates?.[0]?.clone().startOf("month") ?? today.clone().startOf("month")
  }

  const [currentMonth, setCurrentMonth] = useState<Moment>(getInitialMonth)

  useEffect(() => {
    setCurrentMonth(getInitialMonth())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const daysInMonth = currentMonth.daysInMonth()
  const startDay = currentMonth.startOf("month").day()
  const offset = (startDay + 6) % 7 // převedeme neděli=0 na konec

  const monthLabel = currentMonth.format("MMMM YYYY")
  const days: JSX.Element[] = []

  for (let i = 0; i < offset; i++) {
    days.push(<div key={`empty-${i}`} />)
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const date = currentMonth.clone().date(d)
    const dateStr = date.format("YYYY-MM-DD")
    const isLesson = lessonSet.has(dateStr)
    const isSkipped = skippedSet.has(dateStr)
    const isToday = date.isSame(today, "day")

    const tooltip = isSkipped ? "Lekce není" : isLesson && resolvedTime ? `Lekce v ${resolvedTime}` : undefined

    const content = (
      <DayCell key={dateStr} isLesson={isLesson} isSkipped={isSkipped} isToday={isToday}>
        {d}
      </DayCell>
    )

    days.push(
      tooltip ? (
        <Tooltip key={dateStr} title={tooltip}>
          {content}
        </Tooltip>
      ) : (
        <React.Fragment key={dateStr}>{content}</React.Fragment>
      )
    )
  }

  return (
    <div style={{ width: "100%" }}>
      <Navigation>
        <Button size="small" onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, "month"))}>
          ←
        </Button>
        <strong>{monthLabel}</strong>
        <Button size="small" onClick={() => setCurrentMonth(currentMonth.clone().add(1, "month"))}>
          →
        </Button>
      </Navigation>

      <CalendarWrapper>
        {daysOfWeek.map((dayShort, index) => (
          <div className="week-days" key={`dow-${index}`}>
            {dayShort}
          </div>
        ))}
      </CalendarWrapper>

      <CalendarWrapper>{days}</CalendarWrapper>
    </div>
  )
}

export default MiniScheduleCalendar
